<template>
  <v-dialog v-model="vidljiv" max-width="820" scrollable content-class="sudreg-trazilica">
    <v-card>
      <v-card-title class="naslov-kartice">
        Sudski registar
        <span class="dodatak" v-if="stanje.firmi">{{ stanje.firmi }} firmi · preslika {{ datumPreslike }}</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="zatvori"><v-icon small>mdi-close</v-icon></v-btn>
      </v-card-title>

      <div class="pa-3 pb-0">
        <v-text-field
          ref="polje"
          outlined dense hide-details
          v-model="upit"
          label="Naziv firme ili OIB"
          placeholder="npr. koestlin, čakovečki mlinovi, 92803032010"
          prepend-inner-icon="mdi-magnify"
          autocomplete="off"
          clearable
          :loading="trazim"
          @keydown.enter.prevent="trazi"
          @keydown.esc.prevent="zatvori"
        ></v-text-field>
        <div class="uputa">
          Traži se po dijelu naziva, s dijakritikom ili bez nje.
          Same znamenke traže po OIB-u ili matičnom broju.
        </div>
      </div>

      <v-card-text class="popis">
        <div class="obavijest" v-if="greska">{{ greska }}</div>

        <div class="obavijest" v-else-if="prazanRegistar">
          Preslika sudskog registra još nije preuzeta.
          Na poslužitelju treba pustiti <code>node helpers/sudregPreuzmi.js</code>.
        </div>

        <div class="obavijest" v-else-if="upitKratak">Upišite barem tri znaka.</div>

        <div class="obavijest" v-else-if="!trazim && !firme.length && trazeno">
          Nema firme koja odgovara upisanom.
          <div class="sitno">U sudskom registru nema obrta ni fizičkih osoba — njih upišite ručno.</div>
        </div>

        <!-- Cijeli redak je gumb, i doseze se tipkovnicom: unos partnera se
             radi u nizu i ne isplati se svaki put hvatati mis. -->
        <div
          v-for="f in firme"
          :key="f.sudreg.mbs"
          class="redak"
          :class="{neaktivna: !f.sudreg.aktivan}"
          tabindex="0"
          @click="odaberi(f)"
          @keydown.enter.prevent="odaberi(f)"
        >
          <div class="glavno">
            <div class="ime">
              {{ f.partnerName }}
              <span class="znak" v-if="!f.sudreg.aktivan">nije aktivna</span>
            </div>
            <div class="puni" v-if="f.sudreg.nazivPuni && f.sudreg.nazivPuni !== f.partnerName">
              {{ f.sudreg.nazivPuni }}
            </div>
            <div class="uz">
              <span v-if="f.partnerVATID">OIB {{ f.partnerVATID }}</span>
              <span v-if="adresa(f)">{{ adresa(f) }}</span>
              <span v-if="f.partneremail">{{ f.partneremail }}</span>
            </div>
          </div>
          <div class="oblik">
            <span v-if="f.sudreg.pravniOblikKratica">{{ f.sudreg.pravniOblikKratica }}</span>
            <small v-if="f.sudreg.djelatnostSifra">NKD {{ f.sudreg.djelatnostSifra }}</small>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <span class="sitno" v-if="firme.length">
          {{ firme.length }} {{ firme.length === 1 ? 'firma' : 'firmi' }}<span v-if="firme.length >= granica"> (prvih {{ granica }})</span>
        </span>
        <v-spacer></v-spacer>
        <v-btn text @click="zatvori">Odustani</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import SudRegService from '@/services/SudRegService'

// Koliko se ceka nakon zadnje tipke prije trazenja. Kratko da se ne osjeti, a
// dovoljno da se ne salje poziv za svako slovo.
const ODGODA = 350

export default {
  name: 'sudregtrazilica',

  data () {
    return {
      vidljiv: false,
      upit: '',
      firme: [],
      trazim: false,
      trazeno: false,
      greska: '',
      granica: 25,
      stanje: {firmi: 0, preuzeto: null},
      odgoda: null
    }
  },

  computed: {
    // Prazan upit nije "nema rezultata" nego "jos nista nije upisano".
    upitKratak () {
      return this.ocisceno.length > 0 && this.ocisceno.length < 3
    },
    ocisceno () {
      return String(this.upit || '').trim()
    },
    prazanRegistar () {
      return this.stanje.firmi === 0
    },
    datumPreslike () {
      const d = this.stanje.preuzeto
      if (!d) { return '—' }
      const t = new Date(d)
      return isNaN(t.getTime()) ? '—' : t.toLocaleDateString('hr-HR')
    }
  },

  watch: {
    upit () {
      // Trazi se dok se tipka, ali tek kad korisnik zastane.
      clearTimeout(this.odgoda)
      if (this.ocisceno.length < 3) {
        this.firme = []
        this.trazeno = false
        return
      }
      this.odgoda = setTimeout(this.trazi, ODGODA)
    }
  },

  beforeDestroy () {
    clearTimeout(this.odgoda)
  },

  methods: {
    /**
     * @param {string} pocetni  cime se otvara (npr. vec upisani naziv partnera)
     */
    async otvori (pocetni) {
      this.greska = ''
      this.firme = []
      this.trazeno = false
      this.upit = pocetni ? String(pocetni).trim() : ''
      this.vidljiv = true
      await this.$nextTick()
      // Fokus u polje: dijalog se otvara tipkovnicom i odmah se tipka dalje.
      if (this.$refs.polje) { this.$refs.polje.focus() }
      this.ucitajStanje()
    },

    // Koliko je firmi u preslici i kad je zadnji put osvjezena. Bez toga bi
    // prazna preslika izgledala kao "firma ne postoji".
    async ucitajStanje () {
      try {
        const {data} = await SudRegService.stanje()
        this.stanje = {firmi: data.firmi || 0, preuzeto: data.preuzeto || null}
      } catch (err) {
        // Stanje je samo obavijest - ako ne stigne, trazenje i dalje radi.
        this.stanje = {firmi: null, preuzeto: null}
      }
    },

    async trazi () {
      clearTimeout(this.odgoda)
      if (this.ocisceno.length < 3) { return }
      const stoTrazim = this.ocisceno
      this.trazim = true
      this.greska = ''
      try {
        const {data} = await SudRegService.poNazivu({naziv: stoTrazim, koliko: this.granica})
        // Odgovor na stari upit ne smije pregaziti noviji.
        if (stoTrazim !== this.ocisceno) { return }
        this.firme = data.firme || []
        this.trazeno = true
      } catch (err) {
        this.greska = 'Traženje nije uspjelo: ' + (err.message || err)
        this.firme = []
      } finally {
        if (stoTrazim === this.ocisceno) { this.trazim = false }
      }
    },

    adresa (f) {
      const ulica = [f.partnerStreet, f.partnerBuildingNameNumber].filter(x => x).join(' ')
      const mjesto = [f.partnerPostcode, f.partnerCity].filter(x => x).join(' ')
      return [ulica, mjesto].filter(x => x).join(', ')
    },

    odaberi (f) {
      this.$emit('odabrana', f)
      this.zatvori()
    },

    zatvori () {
      clearTimeout(this.odgoda)
      this.vidljiv = false
    }
  }
}
</script>

<style scoped>
.sudreg-trazilica .naslov-kartice {
  font-size: 17px;
  font-weight: 700;
  color: #1565c0;
  background: #eef4fb;
  border-bottom: 2px solid #1976d2;
  padding: 8px 14px;
  min-height: 0;
}
.sudreg-trazilica .naslov-kartice .dodatak {
  font-weight: 400;
  font-size: 13px;
  color: #6b8299;
  margin-left: 10px;
}
.sudreg-trazilica .uputa {
  font-size: 12px;
  color: #78909c;
  padding: 6px 2px 0 2px;
}
.sudreg-trazilica .popis {
  max-height: 52vh;
  padding: 8px 12px 12px 12px;
}
.sudreg-trazilica .obavijest {
  padding: 22px 8px;
  text-align: center;
  color: #607d8b;
}
.sudreg-trazilica .sitno {
  font-size: 12px;
  color: #90a4ae;
  margin-top: 4px;
}
.sudreg-trazilica .redak {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid #e3eaf1;
  border-radius: 4px;
  margin-bottom: 6px;
  cursor: pointer;
}
.sudreg-trazilica .redak:hover,
.sudreg-trazilica .redak:focus {
  background: #eef4fb;
  border-color: #90caf9;
  outline: none;
}
.sudreg-trazilica .redak.neaktivna { opacity: 0.7; }
.sudreg-trazilica .glavno { flex: 1 1 auto; min-width: 0; }
.sudreg-trazilica .ime { font-weight: 600; color: #263238; }
.sudreg-trazilica .ime .znak {
  font-size: 11px;
  font-weight: 600;
  color: #c62828;
  background: #ffebee;
  border-radius: 3px;
  padding: 1px 5px;
  margin-left: 6px;
}
.sudreg-trazilica .puni {
  font-size: 12px;
  color: #78909c;
  margin-top: 1px;
}
.sudreg-trazilica .uz {
  font-size: 12px;
  color: #546e7a;
  margin-top: 3px;
}
.sudreg-trazilica .uz span + span::before {
  content: '·';
  margin: 0 6px;
  color: #b0bec5;
}
.sudreg-trazilica .oblik {
  flex: 0 0 auto;
  text-align: right;
  font-size: 12px;
  color: #546e7a;
}
.sudreg-trazilica .oblik small {
  display: block;
  color: #90a4ae;
  font-size: 11px;
}
</style>
